/**
 * /health — "Your FitScore" screen.
 *
 * Renders the SAME FitScore as Home. There is ONE score in this app,
 * and it comes from GET /api/fitscore/today. The legacy /api/health/dashboard
 * engine (with hardcoded sleep=70, fake body_measurements, fake achievements)
 * was deleted — this file no longer imports HealthRing or reads any
 * `score_breakdown`.
 *
 * Charts (weight/calorie/macro/consistency) come from /api/me/history and
 * are DATA ONLY — nothing here computes a score locally.
 */
import { useEffect, useState } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  ResponsiveContainer, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import { ShoppingBag, ChevronRight, Scale } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import FitScoreHero from "../../components/home/FitScoreHero";
import Heatmap from "../../components/charts/Heatmap";
import WorkoutHistorySection from "../../components/workout/WorkoutHistory";
import HabitCalendar from "../../components/health/HabitCalendar";
import { useNavigate } from "react-router-dom";

export default function HealthDashboard() {
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [proteinGap, setProteinGap] = useState(null);
  const [aiTrainerSummary, setAiTrainerSummary] = useState(null);

  useEffect(() => {
    apiClient.get("/me/history?days=30").then((r) => setHistory(r.data)).catch(() => setHistory({}));
    apiClient.get("/marketplace/context/protein-gap")
      .then((r) => setProteinGap(r.data)).catch(() => setProteinGap(null));
    apiClient.get("/ai-trainer/summary?days=7")
      .then((r) => setAiTrainerSummary(r.data)).catch(() => setAiTrainerSummary(null));
  }, []);

  if (!history) {
    return (
      <div className="app-page" data-testid="health-loading">
        <TopBar title="Your FitScore" />
        <main className="max-w-2xl mx-auto px-4 pt-3 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-white rounded-[16px] border border-line2 animate-pulse" />
          ))}
        </main>
        <BottomNav />
      </div>
    );
  }

  const hasWeightData = Array.isArray(history.weight_trend) && history.weight_trend.length >= 2;

  return (
    <div className="app-page" data-testid="health-dashboard">
      <TopBar title="Your FitScore" />
      <main className="max-w-2xl mx-auto px-4 pt-2 pb-8 space-y-5">
        {/* ONE score — same component as Home, same values, same colours. */}
        <section data-testid="fitscore-section">
          <FitScoreHero />
        </section>

        {/* Weight trend — with proper empty state instead of an empty frame. */}
        <section
          className="bg-white rounded-[16px] p-5 border border-line2"
          style={{ boxShadow: "var(--shadow-card)" }}
          data-testid="weight-trend"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-[20px]">Weight trend</h2>
            {hasWeightData && (
              <div className="text-[13px] text-ink-soft">
                {history.current_weight_kg ? `${Number(history.current_weight_kg).toFixed(1)} kg` : "--"}
                {history.goal_weight_kg ? ` → ${Number(history.goal_weight_kg).toFixed(1)} kg goal` : ""}
              </div>
            )}
          </div>
          {hasWeightData ? (
            <div className="h-44">
              <ResponsiveContainer>
                <LineChart data={history.weight_trend} margin={{ left: 0, right: 0, top: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="lineG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00D4FF" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#00D4FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 2" stroke="#EEECE9" />
                  <XAxis dataKey="date" hide />
                  <YAxis domain={["auto", "auto"]} hide />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E0DC" }} />
                  {history.goal_weight_kg && (
                    <ReferenceLine y={history.goal_weight_kg} stroke="#00FFB3" strokeDasharray="4 4" />
                  )}
                  <Line type="monotone" dataKey="weight_kg" stroke="#00D4FF" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div
              data-testid="weight-trend-empty"
              className="flex flex-col items-center justify-center py-6 text-center"
            >
              <div
                className="w-11 h-11 rounded-full grid place-items-center mb-2"
                style={{ background: "rgba(0,212,255,0.14)" }}
              >
                <Scale size={18} style={{ color: "#00B0D5" }} />
              </div>
              <div className="font-display text-[16px] mb-1">Log your weight to see your trend</div>
              <div className="text-[12px] text-ink-soft mb-3">
                Weekly weigh-ins reveal your real progress.
              </div>
              <button
                type="button"
                onClick={() => navigate("/health/body-scan")}
                data-testid="log-weight-cta"
                className="px-4 h-9 rounded-full text-[13px] font-semibold text-white"
                style={{ background: "#0C0C0B" }}
              >
                Log weight
              </button>
            </div>
          )}
        </section>

        {/* Calorie chart */}
        <section
          className="bg-white rounded-[16px] p-5 border border-line2"
          style={{ boxShadow: "var(--shadow-card)" }}
          data-testid="calorie-chart"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-[20px]">Calories — last 7 days</h2>
          </div>
          <div className="h-40">
            <ResponsiveContainer>
              <BarChart data={history.calorie_chart || []}>
                <CartesianGrid strokeDasharray="2 2" stroke="#EEECE9" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#A09C97" />
                <YAxis fontSize={11} stroke="#A09C97" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E0DC" }} />
                <Bar dataKey="calories" fill="#00D4FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Heatmap */}
        <section
          className="bg-white rounded-[16px] p-5 border border-line2"
          style={{ boxShadow: "var(--shadow-card)" }}
          data-testid="heatmap-section"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-[20px]">Your consistency</h2>
            <div className="flex items-center gap-1.5 text-[11px] text-ink-muted">
              less
              <div className="w-3 h-3 rounded-sm hm-0" />
              <div className="w-3 h-3 rounded-sm hm-1" />
              <div className="w-3 h-3 rounded-sm hm-2" />
              <div className="w-3 h-3 rounded-sm hm-3" />
              <div className="w-3 h-3 rounded-sm hm-4" />
              more
            </div>
          </div>
          <Heatmap data={history.heatmap || []} />
        </section>

        {/* Protein gap — context-aware marketplace card */}
        {proteinGap?.should_show && (
          <button
            type="button"
            onClick={() => navigate(proteinGap.deeplink || "/marketplace?category=supplements")}
            data-testid="protein-gap-card"
            className="w-full text-left bg-white rounded-[16px] p-4 border border-line2 flex items-center gap-3"
            style={{
              boxShadow: "var(--shadow-card)",
              borderLeft: "3px solid #00D4FF",
            }}
          >
            <div
              className="w-11 h-11 rounded-full grid place-items-center shrink-0"
              style={{ background: "rgba(0,212,255,0.14)" }}
            >
              <ShoppingBag size={18} style={{ color: "#00B0D5" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span
                  className="text-[10px] uppercase tracking-[0.12em] font-bold"
                  style={{ color: "#00B0D5" }}
                >
                  From the Shop
                </span>
              </div>
              <div className="font-barlow-cond font-extrabold text-[18px] leading-tight mt-0.5">
                {proteinGap.headline || "Protein below target today"}
              </div>
              <div className="text-[12px] text-ink-soft mt-0.5">
                {proteinGap.sub || `You're at ${proteinGap.protein_today_g}g of ${proteinGap.protein_target_g}g`}
              </div>
            </div>
            <ChevronRight size={18} className="text-ink-tertiary shrink-0" />
          </button>
        )}

        {/* Macros */}
        <section
          className="bg-white rounded-[16px] p-5 border border-line2"
          style={{ boxShadow: "var(--shadow-card)" }}
          data-testid="macros-section"
        >
          <h2 className="font-display text-[20px] mb-3">Macros — last 7 days</h2>
          <div className="h-40">
            <ResponsiveContainer>
              <BarChart data={history.macro_chart || []}>
                <CartesianGrid strokeDasharray="2 2" stroke="#EEECE9" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#A09C97" />
                <YAxis fontSize={11} stroke="#A09C97" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E0DC" }} />
                <Bar dataKey="protein_g" stackId="m" fill="#00FFB3" />
                <Bar dataKey="carbs_g" stackId="m" fill="#00D4FF" />
                <Bar dataKey="fat_g" stackId="m" fill="#A09C97" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 text-[12px] text-ink-soft mt-2">
            <Legend color="#00FFB3" label="Protein" />
            <Legend color="#00D4FF" label="Carbs" />
            <Legend color="#A09C97" label="Fat" />
          </div>
        </section>

        {/* Habits + workout history — genuine data, kept as-is. */}
        <HabitCalendar />
        <WorkoutHistorySection />

        {/* Personal AI Trainer summary — genuine data, guarded on session_count. */}
        {aiTrainerSummary?.session_count > 0 && (
          <section
            className="bg-white rounded-[16px] p-5 border border-line2"
            data-testid="pat-health-section"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-barlow-cond font-extrabold text-[20px]">Personal AI Trainer</h3>
              <span className="text-[11px] text-ink-tertiary">Last 7 days</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div
                  className="font-barlow-cond"
                  style={{
                    fontSize: 32,
                    fontWeight: 900,
                    lineHeight: 1,
                    color:
                      (aiTrainerSummary.today_score || 0) >= 85
                        ? "#00A067"
                        : (aiTrainerSummary.today_score || 0) >= 65
                        ? "#1A1A1A"
                        : "#C08400",
                  }}
                  data-testid="pat-form-score"
                >
                  {aiTrainerSummary.today_score ?? "—"}
                </div>
                <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary mt-1">
                  {(aiTrainerSummary.today_score || 0) >= 75 ? "Good form" : "Watch your form"}
                </div>
              </div>
              <div>
                <div
                  className="font-barlow-cond"
                  style={{ fontSize: 32, fontWeight: 900, lineHeight: 1 }}
                >
                  {aiTrainerSummary.total_reps || 0}
                </div>
                <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-ink-tertiary mt-1">
                  Total reps
                </div>
              </div>
            </div>
            {aiTrainerSummary.most_common_issue && (
              <div
                className="mt-3 text-[12px] text-ink-soft"
                data-testid="pat-common-issue"
              >
                Most common note this month:{" "}
                <span className="font-semibold text-ink-primary">
                  {(aiTrainerSummary.most_common_issue || "").replace(/_/g, " ")}
                </span>
              </div>
            )}
            {aiTrainerSummary.trend?.length > 1 && (
              <div className="mt-3 h-16">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={aiTrainerSummary.trend}>
                    <Line
                      type="monotone"
                      dataKey="form_quality"
                      stroke="#00B0D5"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-sm" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
